'use client';

// useTextContentSave - Auto-save text content to database with debounce
// Story 6.5: Added referrerBotId for viral tracking attribution

import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveSessionToStorage, getOrCreateClientSession } from '../lib/session';
import { getReferral, clearReferral } from '../lib/referralTracking';

interface TextContentSaveResponse {
  data: {
    contentId: string;
    botId: string;
  };
}

interface TextContentSaveError {
  error: {
    code: string;
    message: string;
  };
}

/**
 * Ensures session is synced before saving content
 */
async function ensureSessionSync(): Promise<void> {
  try {
    const localSession = getOrCreateClientSession();
    const response = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: localSession }),
    });
    const data = await response.json();
    if (data.data?.sessionId) {
      saveSessionToStorage(data.data.sessionId);
    }
  } catch {
    console.warn('[session] Pre-save session sync failed');
  }
}

/**
 * Save text content to an existing bot (creates new content)
 */
async function createTextContent(
  botId: string,
  content: string
): Promise<TextContentSaveResponse> {
  await ensureSessionSync();

  const response = await fetch(`/api/bots/${botId}/content`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as TextContentSaveError;
  }

  return {
    data: {
      contentId: data.data.contentId,
      botId,
    },
  };
}

/**
 * Update existing text content
 */
async function updateTextContent(
  botId: string,
  contentId: string,
  content: string
): Promise<TextContentSaveResponse> {
  await ensureSessionSync();

  const response = await fetch(`/api/bots/${botId}/content/${contentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as TextContentSaveError;
  }

  return {
    data: {
      contentId,
      botId,
    },
  };
}

/**
 * Create a temp bot with text content
 */
async function createBotWithText(content: string): Promise<TextContentSaveResponse> {
  await ensureSessionSync();

  // Story 6.5: Get referral attribution for new bot creation
  const referrerBotId = getReferral();

  const response = await fetch('/api/bots/temp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      content,
      // Only include referrerBotId if we have one
      ...(referrerBotId ? { referrerBotId } : {}),
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as TextContentSaveError;
  }

  // Story 6.5: Clear referral after successful bot creation
  if (referrerBotId) {
    clearReferral();
  }

  return {
    data: {
      contentId: data.data.contentId ?? '',
      botId: data.data.botId,
    },
  };
}

export interface UseTextContentSaveOptions {
  /** Existing bot ID to add content to */
  botId: string | null;
  /** Debounce delay in ms */
  debounceMs?: number;
  /** Callback when content is saved successfully */
  onSaved?: (result: { contentId: string; botId: string }) => void;
  /** Callback when save fails */
  onError?: (error: TextContentSaveError) => void;
  /** Minimum content length to trigger save */
  minLength?: number;
}

export interface UseTextContentSaveReturn {
  /** Save content (debounced) */
  saveContent: (content: string) => void;
  /** Force immediate save */
  saveNow: (content: string) => Promise<void>;
  /** Set content ID for editing an existing content source */
  setEditingContent: (contentId: string, content: string) => void;
  /** Reset to create new content (clear editing state) */
  resetEditing: () => void;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Last saved content ID */
  savedContentId: string | null;
  /** Bot ID (created or existing) */
  botId: string | null;
  /** Whether content has been saved */
  hasSaved: boolean;
  /** Whether currently editing existing content */
  isEditing: boolean;
}

/**
 * Hook for auto-saving text content with debounce
 *
 * @example
 * ```tsx
 * const { saveContent, isSaving } = useTextContentSave({
 *   botId: existingBotId,
 *   onSaved: ({ botId }) => setSharedBotId(botId),
 * });
 *
 * // In useEffect watching content changes:
 * useEffect(() => {
 *   if (content.length > 10) {
 *     saveContent(content);
 *   }
 * }, [content, saveContent]);
 * ```
 */
export function useTextContentSave({
  botId: initialBotId,
  debounceMs = 1500,
  onSaved,
  onError,
  minLength = 10,
}: UseTextContentSaveOptions): UseTextContentSaveReturn {
  const [savedContentId, setSavedContentId] = useState<string | null>(null);
  const [currentBotId, setCurrentBotId] = useState<string | null>(initialBotId);
  const [hasSaved, setHasSaved] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedContentRef = useRef<string>('');
  const queryClient = useQueryClient();

  // Refs to avoid stale closures in mutationFn — always hold latest values
  const currentBotIdRef = useRef<string | null>(initialBotId);
  const savedContentIdRef = useRef<string | null>(null);

  // Mutation guard to prevent duplicate bot creation race condition
  const isMutatingRef = useRef(false);
  // Stores latest content that arrived during an in-flight mutation
  const pendingContentRef = useRef<string | null>(null);

  // Update botId when prop changes (external bot ID takes priority)
  useEffect(() => {
    if (initialBotId) {
      setCurrentBotId(initialBotId);
      currentBotIdRef.current = initialBotId;
    }
  }, [initialBotId]);

  const saveMutation = useMutation({
    mutationFn: async (content: string) => {
      isMutatingRef.current = true;
      // Read from refs to avoid stale closure — state may not have updated yet
      const botId = currentBotIdRef.current;
      const contentId = savedContentIdRef.current;
      if (botId && contentId) {
        // Update existing content
        return updateTextContent(botId, contentId, content);
      } else if (botId) {
        // Create new content for existing bot
        return createTextContent(botId, content);
      } else {
        // Create new bot with content
        return createBotWithText(content);
      }
    },
    onSuccess: (result) => {
      setSavedContentId(result.data.contentId);
      savedContentIdRef.current = result.data.contentId;
      if (!currentBotIdRef.current && result.data.botId) {
        setCurrentBotId(result.data.botId);
        currentBotIdRef.current = result.data.botId;
      }
      setHasSaved(true);

      // Invalidate content sources query to refresh the list
      if (result.data.botId) {
        queryClient.invalidateQueries({ queryKey: ['contentSources', result.data.botId] });
      }

      onSaved?.(result.data);
    },
    onError: (error: TextContentSaveError) => {
      onError?.(error);
    },
    onSettled: () => {
      isMutatingRef.current = false;

      // If content arrived during in-flight mutation, save the latest version
      if (pendingContentRef.current) {
        const pending = pendingContentRef.current;
        pendingContentRef.current = null;
        lastSavedContentRef.current = pending;
        saveMutation.mutate(pending);
      }
    },
  });

  const saveNow = useCallback(
    async (content: string) => {
      // Don't save if content is too short or unchanged
      if (content.length < minLength) return;
      if (content === lastSavedContentRef.current) return;

      lastSavedContentRef.current = content;
      await saveMutation.mutateAsync(content);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutateAsync is stable in TanStack Query
    [saveMutation.mutateAsync, minLength]
  );

  const saveContent = useCallback(
    (content: string) => {
      // Clear existing debounce timer
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Don't schedule save if content is too short or unchanged
      if (content.length < minLength) return;
      if (content === lastSavedContentRef.current) return;

      // If a mutation is in flight, queue this content for after it settles
      if (isMutatingRef.current) {
        pendingContentRef.current = content;
        return;
      }

      // Schedule debounced save
      debounceRef.current = setTimeout(() => {
        lastSavedContentRef.current = content;
        saveMutation.mutate(content);
      }, debounceMs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is stable in TanStack Query
    [saveMutation.mutate, debounceMs, minLength]
  );

  // Set content ID for editing an existing content source
  const setEditingContent = useCallback((contentId: string, content: string) => {
    setSavedContentId(contentId);
    savedContentIdRef.current = contentId;
    lastSavedContentRef.current = content;
    setHasSaved(true);
  }, []);

  // Reset editing state to create new content
  const resetEditing = useCallback(() => {
    setSavedContentId(null);
    savedContentIdRef.current = null;
    lastSavedContentRef.current = '';
    setHasSaved(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    saveContent,
    saveNow,
    setEditingContent,
    resetEditing,
    isSaving: saveMutation.isPending,
    savedContentId,
    botId: currentBotId,
    hasSaved,
    isEditing: savedContentId !== null,
  };
}
